/* eslint-disable no-console */
import {
  BalanceSheet,
  CashFlowInventory,
  ContactDetails,
  LeadResult,
  Party,
  Retirement,
  Scenario,
} from '@myfaro/domain';

import { fieldHasError } from '../../helpers';

interface Completed {
  readonly isCompleted?: boolean;
}

export interface WhoI extends Partial<Party>, Completed {}

// TODO: find way to remove double declared fields,
//  should inherit fields from Partial<Party>, Completed
export class Who implements WhoI {
  constructor(allProps: WhoI = {}) {
    Object.assign(this, allProps);
  }

  name?: string;
  civilStatus?: Party.civilStatus;
  dateOfBirth?: string;
  email?: string;
  mobile?: string;
  gender?: Party.gender;
  hasDependentChildren?: boolean;
  isPartnership?: boolean;
  locale?: Party.locale;
  zip_code?: string;
  socialStatus?: Party.socialStatus;
  hasOwnCompany?: boolean;

  // TODO: update this to include all the new field based on config
  public get isCompleted() {
    return (
      !fieldHasError('who', 'name', this.name) &&
      !fieldHasError('who', 'dateOfBirth', this.dateOfBirth) &&
      !fieldHasError('who', 'socialStatus', this.socialStatus)
    );
  }
}

type Inventory = {
  balanceSheet?: Partial<BalanceSheet>;
  cashFlow?: Partial<CashFlowInventory>;
};

export interface WhatI extends Partial<Inventory>, Completed {}

export class What implements WhatI {
  constructor(allProps: WhatI) {
    Object.assign(this, allProps);
  }

  balanceSheet?: Partial<BalanceSheet>;
  cashFlow?: Partial<CashFlowInventory>;

  public get isCompleted() {
    return !!(
      this.balanceSheet?.bankSavingsValue !== undefined ||
      this.balanceSheet?.bankInvestmentsValue !== undefined ||
      this.balanceSheet?.insuranceProtectionValue !== undefined ||
      this.balanceSheet?.businessAssetsValue !== undefined ||
      this.balanceSheet?.homeValue !== undefined ||
      this.balanceSheet?.otherRealEstateValue !== undefined ||
      this.balanceSheet?.liabilitiesValue !== undefined ||
      this.balanceSheet?.monthlyExpensesLiabilitiesValue !== undefined
    );
  }

  public set isCompleted(value: boolean) {}
}

export interface GoalsI extends Partial<Scenario>, Completed {}

export class Goals implements GoalsI {
  constructor(allProps: GoalsI = {}) {
    Object.assign(this, allProps);
  }

  retirement?: Partial<Retirement>;
  monthlySavingsValue?: number;
  notes?: string;
  additionalDomains?: string[];

  public get isCompleted() {
    return !!this.monthlySavingsValue;
  }
}

export interface ContactI extends Partial<ContactDetails>, Completed {}

export class Contact implements ContactI {
  constructor(allProps: ContactI = {}) {
    Object.assign(this, allProps);
  }

  name?: string;
  email?: string;
  mobile?: string;
  wishesContact?: boolean;
  signedTermsAndConditions?: boolean;

  public get isCompleted() {
    return (
      !fieldHasError('contact', 'email', this.email) &&
      !!this.signedTermsAndConditions
    );
  }
}

export interface InfoMsg {
  show: boolean;
  type: 'error' | 'info' | 'success' | 'warning';
  msg: any;
}

export interface ResultI extends Partial<LeadResult>, Completed {}

export class Result implements ResultI {
  constructor(allProps: ResultI = {}) {
    Object.assign(this, allProps);
  }

  gapType?: string;
  gapImportance?: string;
  color?: string;
  liquidAssetsGraph?: any;
  confidenceScore?: number;
  actionIntro?: string;
  actionDetails?: Array<ActionDetail>;
  balanceSheetAtRetirement?: BalanceSheetAtRetirement;
  isDirty?: boolean;
  actionTitle?: string;
  actionImageUrl?: string;
  domainFollowUpDetails?: any;
  domainFollowUpTitle?: any;
  actionDetailsTitle?: string;

  public get isCompleted() {
    return true;
  }
}

export interface ActionDetail {
  actionTitle: string;
  actionDescription: string;
  actionUrl: string;
}

export interface BalanceSheetAtRetirement {
  assets: object;
  liabilities: object;
}

export interface Profile {
  who: Who;
  what: What;
  goals: Goals;
  contact: Contact;
  result?: Result;
}

export type CurrentStep =
  | 'home'
  | 'who'
  | 'what'
  | 'goals'
  | 'contact'
  | 'result';

export type ScenarioType = {
  name: keyof Retirement;
  rightIcon: JSX.Element;
  type: 'slider' | 'valueInput';
  rangeValues: { min: number; max: number; step: number };
  basicValue: number | undefined;
};

export type Field =
  | 'name'
  | 'dateOfBirth'
  | 'email'
  | 'mobile'
  | 'socialStatus'
  | 'bankSavingsValue'
  | 'bankInvestmentsValue'
  | 'insuranceProtectionValue'
  | 'businessAssetsValue'
  | 'homeValue'
  | 'liabilitiesValue'
  | 'name';

export type Balance = {
  activa: number;
  passiva: number;
};

export type AdditionalDomainsType = {
  name: 'invest_real_estate' | 'maintain_standard_of_living';
  slider: boolean;
};

export type MovablePropertyType = {
  name: keyof BalanceSheet;
  label: string;
  tooltipTitle: string;
};

export type ImmovablePropertyType = {
  name: keyof BalanceSheet;
  label: string;
  tooltipTitle: string;
};

export enum GenderValues {
  Male = 'male',
  Female = 'female',
  Other = 'other',
}

export type ExtraCheckedFields = 'email' | 'mobile';

export * from './LocalStorageKey';
